import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { saveAs } from 'file-saver';
import { IChecklistTicket } from '../interfaces/checklist-ticket.interface';
import { IChecklistTicketAnswer } from '../interfaces/checklist-ticket-answer.interface';
import { IQuestion, IQuestionResponse } from '../interfaces/question.interface';

export interface ICsvChecklistRow {
  ticketNumber: string;
  ticketType: string;
  ticketSubtype: string;
  branch: string;
  sector: string;
  fullName: string;
  question: string;
  answer: string;
  comment: string;
  choiceComments?: { choice: string; comment: string }[];
}

interface ILoadedPhotoItem {
  code: string; // e.g. 'Ф-01'
  questionIndex: number; // 1-based row number
  questionText: string;
  comment: string;
  rawUrl: string;
  fileName: string;
  image: HTMLImageElement | null;
}

@Injectable({
  providedIn: 'root',
})
export class ChecklistPdfGeneratorService {
  constructor(private http: HttpClient) {}

  public async generateAndSavePdf(csvBlob: Blob, ticket: IChecklistTicket): Promise<void> {
    const csvText = await this.decodeCsvBlob(csvBlob);
    const rows = this.parseCsv(csvText);
    await this.prepareChoiceComments(rows, ticket);
    const pdfBlob = await this.buildPdfBlob(rows, ticket);
    const ticketNum = ticket?.hd_ticket_number || rows[0]?.ticketNumber || 'ticket';
    saveAs(pdfBlob, `ticket_${ticketNum}_checklist.pdf`);
  }

  private async prepareChoiceComments(rows: ICsvChecklistRow[], ticket: IChecklistTicket): Promise<void> {
    const structuredRows = rows.filter((row) => {
      try {
        const value = JSON.parse(row.comment);
        if (!value || typeof value !== 'object' || Array.isArray(value) || !Object.entries(value).every(([id, comment]) => /^\d+$/.test(id) && typeof comment === 'string')) {
          return false;
        }
        row.choiceComments = Object.entries(value).map(([id, comment]) => ({ choice: `Вариант ${id}`, comment: String(comment) }));
        return true;
      } catch {
        return false;
      }
    });
    if (!structuredRows.length) {
      return;
    }
    let answers: IChecklistTicketAnswer[] = [];
    if (ticket.id) {
      try {
        const response = await firstValueFrom(this.http.get<{ data: IChecklistTicketAnswer[] }>(`/checklist/ticket/${ticket.id}/answers/`));
        answers = response.data;
      } catch {
        // Reports may use a departure ID rather than a checklist ticket ID.
      }
    }
    await Promise.all(
      structuredRows.map(async (row) => {
        try {
          const matches = answers.filter((answer) => answer.question_text === row.question);
          let question: IQuestion;
          if (matches.length === 1) {
            try {
              const response = await firstValueFrom(this.http.get<{ data: IQuestion }>(`/checklist/questions/${matches[0].question}/`));
              question = response.data;
            } catch {
              // Resolve by choice IDs below if the detail endpoint is unavailable.
            }
          }
          if (!question) {
            const ids = Object.keys(JSON.parse(row.comment));
            const params = new HttpParams().set('search', row.question).set('limit', '100').set('offset', '0');
            const response = await firstValueFrom(this.http.get<{ data: IQuestionResponse }>('/checklist/questions', { params }));
            const candidates = response.data.results.filter(
              (item) => item.text.trim() === row.question.trim() && ids.every((id) => item.answer_choices.some((choice) => String(choice.id) === id))
            );
            if (candidates.length !== 1) {
              return;
            }
            question = candidates[0];
          }
          const comments: Record<string, string> = JSON.parse(row.comment);
          const selected = matches.length === 1 ? matches[0].answer_choices : [];
          const choices = selected.length
            ? selected.map((value) => question.answer_choices.find((choice) => choice.text === value || String(choice.id) === String(value)))
            : question.answer_choices.filter((choice) => Object.prototype.hasOwnProperty.call(comments, choice.id));
          row.choiceComments = choices.filter(Boolean).map((choice) => ({ choice: choice.text, comment: comments[choice.id] || '' }));
          for (const [id, comment] of Object.entries(comments)) {
            if (!choices.some((choice) => choice && String(choice.id) === id)) {
              const choice = question.answer_choices.find((item) => String(item.id) === id);
              row.choiceComments.push({ choice: choice?.text || `Вариант ${id}`, comment });
            }
          }
        } catch {
          // Retain ID labels if metadata is unavailable; never guess by position.
        }
      })
    );
  }

  private async decodeCsvBlob(blob: Blob): Promise<string> {
    const buffer = await new Response(blob).arrayBuffer();
    const uint8 = new Uint8Array(buffer);

    try {
      const utf8Decoder = new TextDecoder('utf-8', { fatal: true });
      return utf8Decoder.decode(uint8);
    } catch {
      const cp1251Decoder = new TextDecoder('windows-1251');
      return cp1251Decoder.decode(uint8);
    }
  }

  private parseCsv(csvText: string): ICsvChecklistRow[] {
    const rawLines = this.splitCsvLines(csvText.replace(/^\uFEFF/, '').trim());
    if (!rawLines.length) {
      return [];
    }

    const headerCells = this.parseCsvLine(rawLines[0]).map((h) => h.trim().toLowerCase());
    const idxMap = {
      ticketNumber: this.findColIndex(headerCells, ['номер заявки', 'заявка'], 0),
      ticketType: this.findColIndex(headerCells, ['тип заявки', 'тип'], 1),
      ticketSubtype: this.findColIndex(headerCells, ['подтип заявки', 'подтип'], 2),
      branch: this.findColIndex(headerCells, ['филиал'], 3),
      sector: this.findColIndex(headerCells, ['участок'], 4),
      fullName: this.findColIndex(headerCells, ['фио', 'исполнитель', 'сотрудник'], 5),
      question: this.findColIndex(headerCells, ['вопрос', 'работа'], 6),
      answer: this.findColIndex(headerCells, ['ответ'], 7),
      comment: this.findColIndex(headerCells, ['комментарий', 'примечание'], 8),
    };

    const result: ICsvChecklistRow[] = [];
    for (let i = 1; i < rawLines.length; i++) {
      const line = rawLines[i].trim();
      if (!line) {
        continue;
      }
      const cells = this.parseCsvLine(rawLines[i]);
      result.push({
        ticketNumber: (cells[idxMap.ticketNumber] || '').trim(),
        ticketType: (cells[idxMap.ticketType] || '').trim(),
        ticketSubtype: (cells[idxMap.ticketSubtype] || '').trim(),
        branch: (cells[idxMap.branch] || '').trim(),
        sector: (cells[idxMap.sector] || '').trim(),
        fullName: (cells[idxMap.fullName] || '').trim(),
        question: (cells[idxMap.question] || '').trim(),
        answer: (cells[idxMap.answer] || '').trim(),
        comment: (cells[idxMap.comment] || '').trim(),
      });
    }

    return result;
  }

  private findColIndex(headers: string[], candidates: string[], fallback: number): number {
    const found = headers.findIndex((h) => candidates.some((c) => h === c || h.includes(c)));
    return found !== -1 ? found : fallback;
  }

  private splitCsvLines(text: string): string[] {
    const lines: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
        current += ch;
      } else if ((ch === '\n' || ch === '\r') && !inQuotes) {
        if (ch === '\r' && text[i + 1] === '\n') {
          i++;
        }
        lines.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    if (current) {
      lines.push(current);
    }
    return lines;
  }

  private parseCsvLine(line: string, delimiter = ';'): string[] {
    const cells: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === delimiter && !inQuotes) {
        cells.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    cells.push(current);
    return cells;
  }

  private extractImageUrls(answer: string): string[] {
    if (!answer) {
      return [];
    }
    const urlMatches = answer.match(/https?:\/\/[^\s,;"]+/gi) || [];
    return urlMatches.filter((url) => /\/images\/|\.(jpg|jpeg|png|webp|gif|bmp)(\?|$)/i.test(url));
  }

  private toApiRequestUrl(rawUrl: string): string {
    try {
      const parsed = new URL(rawUrl);
      const apiPrefixIdx = parsed.pathname.indexOf('/api/v0/');
      if (apiPrefixIdx !== -1) {
        // Strip '/api/v0' so ApiPrefixInterceptor prepends environment.apiPrefix and uses dev proxy/cookies
        return parsed.pathname.slice(apiPrefixIdx + '/api/v0'.length) + parsed.search;
      }
      return parsed.pathname + parsed.search;
    } catch {
      return rawUrl;
    }
  }

  private async fetchImageElement(rawUrl: string): Promise<HTMLImageElement | null> {
    try {
      const requestUrl = this.toApiRequestUrl(rawUrl);
      const blob = await firstValueFrom(
        this.http.get(requestUrl, {
          responseType: 'blob',
        })
      );
      return await this.blobToImage(blob);
    } catch {
      return null;
    }
  }

  private blobToImage(blob: Blob): Promise<HTMLImageElement | null> {
    return new Promise((resolve) => {
      const objectUrl = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      };
      img.src = objectUrl;
    });
  }

  private async collectAndLoadPhotos(rows: ICsvChecklistRow[]): Promise<{
    photos: ILoadedPhotoItem[];
    rowPhotoCodes: Map<number, string[]>;
  }> {
    const photos: ILoadedPhotoItem[] = [];
    const rowPhotoCodes = new Map<number, string[]>();
    let photoCounter = 1;

    rows.forEach((row, idx) => {
      const qIndex = idx + 1;
      const urls = this.extractImageUrls(row.answer);
      if (!urls.length) {
        return;
      }
      const codes: string[] = [];
      for (const rawUrl of urls) {
        const code = `Ф-${String(photoCounter).padStart(2, '0')}`;
        photoCounter++;
        codes.push(code);
        const parts = rawUrl.split('/').filter(Boolean);
        const fileName = parts[parts.length - 1] || code;
        photos.push({
          code,
          questionIndex: qIndex,
          questionText: row.question || `Пункт ${qIndex}`,
          comment: row.comment || '',
          rawUrl,
          fileName,
          image: null,
        });
      }
      rowPhotoCodes.set(qIndex, codes);
    });

    await Promise.all(
      photos.map(async (item) => {
        item.image = await this.fetchImageElement(item.rawUrl);
      })
    );

    return { photos, rowPhotoCodes };
  }

  private async buildPdfBlob(rows: ICsvChecklistRow[], ticket: IChecklistTicket): Promise<Blob> {
    const PAGE_W = 1240;
    const PAGE_H = 1754;
    const MARGIN_X = 64;
    const MARGIN_TOP = 64;
    const MARGIN_BOTTOM = 72;
    const CONTENT_W = PAGE_W - MARGIN_X * 2;

    const { photos, rowPhotoCodes } = await this.collectAndLoadPhotos(rows);

    const canvases: HTMLCanvasElement[] = [];
    let canvas = this.createPageCanvas(PAGE_W, PAGE_H);
    let ctx = canvas.getContext('2d');
    canvases.push(canvas);

    let y = MARGIN_TOP;

    const startNewPage = () => {
      canvas = this.createPageCanvas(PAGE_W, PAGE_H);
      ctx = canvas.getContext('2d');
      canvases.push(canvas);
      y = MARGIN_TOP;
    };

    const ensureSpace = (neededHeight: number, repeatTableHeader = false) => {
      if (y + neededHeight > PAGE_H - MARGIN_BOTTOM) {
        startNewPage();
        if (repeatTableHeader) {
          y = this.drawQuestionsTableHeader(ctx, MARGIN_X, y, CONTENT_W);
        }
      }
    };

    const firstRow = rows[0];
    const ticketNumber = String(ticket?.hd_ticket_number || firstRow?.ticketNumber || '—');
    const ticketType = ticket?.ticket_type_name || firstRow?.ticketType || '—';
    const ticketSubtype = firstRow?.ticketSubtype || '—';
    const branch = ticket?.mb_name || firstRow?.branch || '—';
    const sector = ticket?.sector_number || firstRow?.sector || '—';
    const brigade = ticket?.brigade_name || '—';
    const fullName = ticket?.employee?.full_name || firstRow?.fullName || '—';
    const employeeNumber = ticket?.employee?.employee_number || '';
    const executorStr = employeeNumber ? `${fullName}, таб. № ${employeeNumber}` : fullName;
    const nowStr = this.formatNowDate();

    // Title
    ctx.fillStyle = '#1f242b';
    ctx.font = 'bold 30px Arial, sans-serif';
    ctx.fillText(`Чек-лист по заявке № ${ticketNumber}`, MARGIN_X, y + 28);
    y += 42;

    // Subtitle
    ctx.fillStyle = '#5a6270';
    ctx.font = '18px Arial, sans-serif';
    const subtitleParts = [`Тип заявки: ${ticketType}${ticketSubtype && ticketSubtype !== '—' ? ' / ' + ticketSubtype : ''}`];
    if (photos.length) {
      subtitleParts.push(`Фотоматериалы — Приложение Б (${photos.length} шт.)`);
    }
    ctx.fillText(subtitleParts.join('. '), MARGIN_X, y + 16);
    y += 34;

    // Metadata Table
    const photoSummary = photos.length
      ? `${photos.length} файл(ов) (${photos.map((p) => p.code).join(', ')})`
      : ticket?.has_checklist_images
        ? 'Приложены фотоматериалы'
        : 'Фото нет';

    const metaRows: Array<{ type: 'single'; label: string; value: string } | { type: 'double'; label1: string; value1: string; label2: string; value2: string }> = [
      { type: 'double', label1: '№ заявки', value1: ticketNumber, label2: 'Тип заявки', value2: ticketType },
      { type: 'double', label1: 'Подтип заявки', value1: ticketSubtype, label2: 'Филиал', value2: branch },
      { type: 'double', label1: 'Участок', value1: sector, label2: 'Бригада', value2: brigade },
      { type: 'single', label: 'Исполнитель (ФИО, таб. №)', value: executorStr },
      {
        type: 'double',
        label1: 'Фото приложено',
        value1: photoSummary,
        label2: 'Дата выгрузки',
        value2: nowStr,
      },
    ];

    y = this.drawMetaTable(ctx, MARGIN_X, y, CONTENT_W, metaRows);
    y += 28;

    // Section Header Bar
    y = this.drawSectionBanner(ctx, MARGIN_X, y, CONTENT_W, '1. Выполненные работы / Ответы по чек-листу');

    // Questions Table Header
    y = this.drawQuestionsTableHeader(ctx, MARGIN_X, y, CONTENT_W);

    if (!rows.length) {
      this.drawCellRect(ctx, MARGIN_X, y, CONTENT_W, 56, '#ffffff', '#b8bec7');
      ctx.fillStyle = '#6c757d';
      ctx.font = 'italic 18px Arial, sans-serif';
      ctx.fillText('Нет данных по ответам чек-листа', MARGIN_X + 18, y + 34);
      y += 56;
    } else {
      rows.forEach((row, idx) => {
        const qIndex = idx + 1;
        const photoCodes = rowPhotoCodes.get(qIndex) || [];
        const rowMetrics = this.measureQuestionRow(ctx, CONTENT_W, qIndex, row, photoCodes);
        ensureSpace(rowMetrics.totalHeight, true);
        y = this.drawQuestionRow(ctx, MARGIN_X, y, CONTENT_W, qIndex, row, rowMetrics, photoCodes);
      });
    }

    // Photo Appendix ("Приложение Б. Фотофиксация") if there are photos
    if (photos.length > 0) {
      y += 32;
      ensureSpace(580, false);

      ctx.fillStyle = '#1f242b';
      ctx.font = 'bold 24px Arial, sans-serif';
      ctx.fillText('Приложение Б. Фотофиксация', MARGIN_X, y + 24);
      y += 32;

      ctx.fillStyle = '#495057';
      ctx.beginPath();
      ctx.moveTo(MARGIN_X, y);
      ctx.lineTo(MARGIN_X + CONTENT_W, y);
      ctx.strokeStyle = '#343a40';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      y += 22;

      ctx.fillStyle = '#5a6270';
      ctx.font = 'italic 16px Arial, sans-serif';
      ctx.fillText('Каждый кадр сопоставлен с пунктом чек-листа.', MARGIN_X, y);
      y += 22;

      const pairRowH = 490;
      for (let i = 0; i < photos.length; i += 2) {
        ensureSpace(pairRowH, false);
        const leftPhoto = photos[i];
        const rightPhoto = photos[i + 1] || null;
        this.drawPhotoGridRow(ctx, MARGIN_X, y, CONTENT_W, pairRowH, leftPhoto, rightPhoto);
        y += pairRowH;
      }
    }

    y += 32;

    // Signature block at the bottom
    ensureSpace(130, false);
    y = this.drawSignatureBlock(ctx, MARGIN_X, y, CONTENT_W, fullName, nowStr.split(',')[0]);

    const jpegPages = canvases.map((c) => this.canvasToJpegBytes(c, 0.92));
    return this.assemblePdfFromJpegs(jpegPages, PAGE_W, PAGE_H);
  }

  private drawPhotoGridRow(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, rowH: number, left: ILoadedPhotoItem, right: ILoadedPhotoItem | null): void {
    const halfW = Math.floor(width / 2);
    this.drawSinglePhotoCard(ctx, x, y, halfW, rowH, left);
    this.drawSinglePhotoCard(ctx, x + halfW, y, width - halfW, rowH, right);
  }

  private drawSinglePhotoCard(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, item: ILoadedPhotoItem | null): void {
    this.drawCellRect(ctx, x, y, w, h, '#ffffff', '#b8bec7');
    const pad = 16;
    const imgBoxW = w - pad * 2;
    const imgBoxH = 330;
    const imgBoxX = x + pad;
    const imgBoxY = y + pad;

    if (!item) {
      // Reserve box like in the template
      ctx.fillStyle = '#f8f9fa';
      ctx.fillRect(imgBoxX, imgBoxY, imgBoxW, h - pad * 2);
      ctx.save();
      ctx.setLineDash([5, 5]);
      ctx.strokeStyle = '#ced4da';
      ctx.strokeRect(imgBoxX, imgBoxY, imgBoxW, h - pad * 2);
      ctx.restore();

      ctx.fillStyle = '#adb5bd';
      ctx.font = '16px Arial, sans-serif';
      const txt = 'резерв';
      const tw = ctx.measureText(txt).width;
      ctx.fillText(txt, imgBoxX + (imgBoxW - tw) / 2, y + h / 2);
      return;
    }

    // Image background box
    ctx.fillStyle = '#f1f3f5';
    ctx.fillRect(imgBoxX, imgBoxY, imgBoxW, imgBoxH);
    ctx.strokeStyle = '#dee2e6';
    ctx.lineWidth = 1;
    ctx.strokeRect(imgBoxX, imgBoxY, imgBoxW, imgBoxH);

    if (item.image && item.image.width > 0 && item.image.height > 0) {
      const scale = Math.min(imgBoxW / item.image.width, imgBoxH / item.image.height);
      const drawW = item.image.width * scale;
      const drawH = item.image.height * scale;
      const drawX = imgBoxX + (imgBoxW - drawW) / 2;
      const drawY = imgBoxY + (imgBoxH - drawH) / 2;
      ctx.drawImage(item.image, drawX, drawY, drawW, drawH);
    } else {
      ctx.fillStyle = '#868e96';
      ctx.font = '15px Arial, sans-serif';
      const fallbackTxt = 'Не удалось загрузить изображение';
      const ftw = ctx.measureText(fallbackTxt).width;
      ctx.fillText(fallbackTxt, imgBoxX + (imgBoxW - ftw) / 2, imgBoxY + imgBoxH / 2);
    }

    // Caption area below image
    let capY = imgBoxY + imgBoxH + 24;
    ctx.fillStyle = '#1f242b';
    ctx.font = 'bold 16px Arial, sans-serif';
    const codeLabel = `${item.code} · п.${item.questionIndex}`;
    ctx.fillText(codeLabel, imgBoxX, capY);
    const codeLabelW = ctx.measureText(codeLabel).width;

    // Small badge "ФОТОФИКСАЦИЯ"
    this.drawStatusBadge(ctx, imgBoxX + codeLabelW + 12, capY - 17, 'ФОТОФИКСАЦИЯ', true);

    capY += 22;
    ctx.fillStyle = '#868e96';
    ctx.font = '14px monospace';
    const shortFile = item.fileName.length > 48 ? item.fileName.slice(0, 45) + '...' : item.fileName;
    ctx.fillText(shortFile, imgBoxX, capY);

    capY += 22;
    ctx.fillStyle = '#1f242b';
    ctx.font = '15px Arial, sans-serif';
    const descText = item.comment ? `${item.questionText} — ${item.comment}` : item.questionText;
    const descLines = this.wrapText(ctx, descText, imgBoxW).slice(0, 3);
    descLines.forEach((line, idx) => {
      ctx.fillText(line, imgBoxX, capY + idx * 20);
    });
  }

  private createPageCanvas(width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    return canvas;
  }

  private drawMetaTable(
    ctx: CanvasRenderingContext2D,
    x: number,
    startY: number,
    width: number,
    rows: Array<{ type: 'single'; label: string; value: string } | { type: 'double'; label1: string; value1: string; label2: string; value2: string }>
  ): number {
    let y = startY;
    const labelW = 210;
    const halfW = Math.floor(width / 2);
    const padX = 14;
    const padY = 12;
    const lineHeight = 23;

    for (const row of rows) {
      if (row.type === 'single') {
        const valW = width - labelW;
        ctx.font = '16px Arial, sans-serif';
        const labelLines = this.wrapText(ctx, row.label, labelW - padX * 2);
        ctx.font = 'bold 18px Arial, sans-serif';
        const valLines = this.wrapText(ctx, row.value || '—', valW - padX * 2);
        const rowH = Math.max(46, Math.max(labelLines.length, valLines.length) * lineHeight + padY * 2);

        this.drawCellRect(ctx, x, y, labelW, rowH, '#f5f6f8', '#b8bec7');
        this.drawCellRect(ctx, x + labelW, y, valW, rowH, '#ffffff', '#b8bec7');

        ctx.fillStyle = '#495057';
        ctx.font = '16px Arial, sans-serif';
        labelLines.forEach((line, i) => ctx.fillText(line, x + padX, y + padY + 17 + i * lineHeight));

        ctx.fillStyle = '#1f242b';
        ctx.font = 'bold 18px Arial, sans-serif';
        valLines.forEach((line, i) => ctx.fillText(line, x + labelW + padX, y + padY + 18 + i * lineHeight));

        y += rowH;
      } else {
        const val1W = halfW - labelW;
        const val2W = width - halfW - labelW;

        ctx.font = '16px Arial, sans-serif';
        const l1Lines = this.wrapText(ctx, row.label1, labelW - padX * 2);
        const l2Lines = this.wrapText(ctx, row.label2, labelW - padX * 2);

        ctx.font = 'bold 18px Arial, sans-serif';
        const v1Lines = this.wrapText(ctx, row.value1 || '—', val1W - padX * 2);
        const v2Lines = this.wrapText(ctx, row.value2 || '—', val2W - padX * 2);

        const maxLines = Math.max(l1Lines.length, l2Lines.length, v1Lines.length, v2Lines.length);
        const rowH = Math.max(46, maxLines * lineHeight + padY * 2);

        this.drawCellRect(ctx, x, y, labelW, rowH, '#f5f6f8', '#b8bec7');
        this.drawCellRect(ctx, x + labelW, y, val1W, rowH, '#ffffff', '#b8bec7');
        this.drawCellRect(ctx, x + halfW, y, labelW, rowH, '#f5f6f8', '#b8bec7');
        this.drawCellRect(ctx, x + halfW + labelW, y, val2W, rowH, '#ffffff', '#b8bec7');

        ctx.fillStyle = '#495057';
        ctx.font = '16px Arial, sans-serif';
        l1Lines.forEach((line, i) => ctx.fillText(line, x + padX, y + padY + 17 + i * lineHeight));
        l2Lines.forEach((line, i) => ctx.fillText(line, x + halfW + padX, y + padY + 17 + i * lineHeight));

        ctx.fillStyle = '#1f242b';
        ctx.font = 'bold 18px Arial, sans-serif';
        v1Lines.forEach((line, i) => ctx.fillText(line, x + labelW + padX, y + padY + 18 + i * lineHeight));
        v2Lines.forEach((line, i) => ctx.fillText(line, x + halfW + labelW + padX, y + padY + 18 + i * lineHeight));

        y += rowH;
      }
    }

    return y;
  }

  private drawSectionBanner(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, title: string): number {
    const h = 44;
    this.drawCellRect(ctx, x, y, width, h, '#e8edf3', '#b8bec7');
    ctx.fillStyle = '#4a6785';
    ctx.fillRect(x, y, 5, h);

    ctx.fillStyle = '#1f242b';
    ctx.font = 'bold 19px Arial, sans-serif';
    ctx.fillText(title, x + 16, y + 29);
    return y + h;
  }

  private drawQuestionsTableHeader(ctx: CanvasRenderingContext2D, x: number, y: number, width: number): number {
    const h = 42;
    const colNumW = 54;
    const colStatusW = 180;
    const colAnswerW = 340;
    const colQuestionW = width - colNumW - colAnswerW - colStatusW;

    this.drawCellRect(ctx, x, y, colNumW, h, '#eef0f4', '#b8bec7');
    this.drawCellRect(ctx, x + colNumW, y, colQuestionW, h, '#eef0f4', '#b8bec7');
    this.drawCellRect(ctx, x + colNumW + colQuestionW, y, colAnswerW, h, '#eef0f4', '#b8bec7');
    this.drawCellRect(ctx, x + colNumW + colQuestionW + colAnswerW, y, colStatusW, h, '#eef0f4', '#b8bec7');

    ctx.fillStyle = '#1f242b';
    ctx.font = 'bold 16px Arial, sans-serif';
    ctx.fillText('№', x + 16, y + 27);
    ctx.fillText('Вопрос / Работа', x + colNumW + 14, y + 27);
    ctx.fillText('Ответ', x + colNumW + colQuestionW + 14, y + 27);
    ctx.fillText('Фото / Статус', x + colNumW + colQuestionW + colAnswerW + 14, y + 27);

    return y + h;
  }

  private measureQuestionRow(
    ctx: CanvasRenderingContext2D,
    width: number,
    index: number,
    row: ICsvChecklistRow,
    photoCodes: string[]
  ): {
    colNumW: number;
    colQuestionW: number;
    colAnswerW: number;
    colStatusW: number;
    questionLines: string[];
    answerLines: string[];
    commentLines: string[];
    photoCodeLines: string[];
    isYesNo: boolean;
    mainRowH: number;
    commentRowH: number;
    totalHeight: number;
  } {
    const colNumW = 54;
    const colStatusW = 180;
    const colAnswerW = 340;
    const colQuestionW = width - colNumW - colAnswerW - colStatusW;
    const padX = 14;
    const padY = 14;
    const lineHeight = 24;

    ctx.font = '18px Arial, sans-serif';
    const questionLines = this.wrapText(ctx, row.question || '—', colQuestionW - padX * 2);

    const normalizedAnswer = (row.answer || '').trim().toLowerCase();
    const isYesNo = normalizedAnswer === 'да' || normalizedAnswer === 'нет';

    const displayAnswer = photoCodes.length
      ? `Фото приложено (${photoCodes.join(', ')}) — см. Приложение Б`
      : row.choiceComments
        ? row.choiceComments.map((item) => `${item.choice}\nКомментарий: ${item.comment}`).join('\n\n') || row.answer || '—'
        : row.answer || '—';

    ctx.font = 'bold 17px Arial, sans-serif';
    const answerLines = isYesNo ? ['да', 'нет'] : this.wrapText(ctx, displayAnswer, colAnswerW - padX * 2);

    const qHeight = questionLines.length * lineHeight + padY * 2;
    const aHeight = (isYesNo ? 2 * 28 : answerLines.length * lineHeight) + padY * 2;
    ctx.font = '15px Arial, sans-serif';
    const photoCodeLines = this.wrapText(ctx, photoCodes.join(', '), colStatusW - padX * 2);
    const sHeight = photoCodes.length ? photoCodeLines.length * lineHeight + 28 + padY * 2 : 50;
    const mainRowH = Math.max(68, qHeight, aHeight, sHeight);

    let commentLines: string[] = [];
    let commentRowH = 0;
    if (row.comment && !row.choiceComments) {
      ctx.font = '17px Arial, sans-serif';
      commentLines = this.wrapText(ctx, row.comment, width - colNumW - padX * 2);
      commentRowH = 30 + commentLines.length * lineHeight + padY * 2;
    }

    return {
      colNumW,
      colQuestionW,
      colAnswerW,
      colStatusW,
      questionLines,
      answerLines,
      commentLines,
      photoCodeLines,
      isYesNo,
      mainRowH,
      commentRowH,
      totalHeight: mainRowH + commentRowH,
    };
  }

  private drawQuestionRow(
    ctx: CanvasRenderingContext2D,
    x: number,
    startY: number,
    width: number,
    index: number,
    row: ICsvChecklistRow,
    m: ReturnType<ChecklistPdfGeneratorService['measureQuestionRow']>,
    photoCodes: string[]
  ): number {
    let y = startY;
    const padX = 14;
    const padY = 14;
    const lineHeight = 24;

    const totalNumCellH = m.mainRowH + m.commentRowH;
    this.drawCellRect(ctx, x, y, m.colNumW, totalNumCellH, '#ffffff', '#b8bec7');
    this.drawCellRect(ctx, x + m.colNumW, y, m.colQuestionW, m.mainRowH, '#ffffff', '#b8bec7');
    this.drawCellRect(ctx, x + m.colNumW + m.colQuestionW, y, m.colAnswerW, m.mainRowH, '#ffffff', '#b8bec7');
    this.drawCellRect(ctx, x + m.colNumW + m.colQuestionW + m.colAnswerW, y, m.colStatusW, m.mainRowH, '#ffffff', '#b8bec7');

    // Number
    ctx.fillStyle = '#495057';
    ctx.font = '17px Arial, sans-serif';
    ctx.fillText(String(index), x + 18, y + padY + 18);

    // Question
    ctx.fillStyle = '#1f242b';
    ctx.font = '18px Arial, sans-serif';
    m.questionLines.forEach((line, i) => {
      ctx.fillText(line, x + m.colNumW + padX, y + padY + 18 + i * lineHeight);
    });

    // Answer
    const ansX = x + m.colNumW + m.colQuestionW + padX;
    if (m.isYesNo) {
      const isYes = (row.answer || '').trim().toLowerCase() === 'да';
      this.drawCheckboxItem(ctx, ansX, y + padY + 4, 'да', isYes);
      this.drawCheckboxItem(ctx, ansX, y + padY + 32, 'нет', !isYes);
    } else {
      ctx.fillStyle = '#1f242b';
      ctx.font = 'bold 17px Arial, sans-serif';
      m.answerLines.forEach((line, i) => {
        ctx.fillText(line, ansX, y + padY + 18 + i * lineHeight);
      });
    }

    // Status column (with photo codes if present, like in the template)
    const statusX = x + m.colNumW + m.colQuestionW + m.colAnswerW + padX;
    const hasAnswer = Boolean((row.answer || '').trim());
    if (photoCodes.length) {
      ctx.fillStyle = '#1f242b';
      ctx.font = '15px Arial, sans-serif';
      m.photoCodeLines.forEach((line, i) => {
        ctx.fillText(line, statusX, y + padY + 16 + i * lineHeight);
      });
      this.drawStatusBadge(ctx, statusX, y + padY + m.photoCodeLines.length * lineHeight + 2, 'ПОДТВЕРЖДЕНО', true);
    } else {
      this.drawStatusBadge(ctx, statusX, y + padY + 2, hasAnswer ? 'ВЫПОЛНЕНО' : 'НЕТ ОТВЕТА', hasAnswer);
    }

    y += m.mainRowH;

    // Optional comment row
    if (m.commentRowH > 0) {
      const commentW = width - m.colNumW;
      this.drawCellRect(ctx, x + m.colNumW, y, commentW, m.commentRowH, '#fafbfc', '#b8bec7');

      ctx.fillStyle = '#5a6270';
      ctx.font = '16px Arial, sans-serif';
      ctx.fillText('Ссылка на фото / комментарий:', x + m.colNumW + padX, y + padY + 16);

      ctx.fillStyle = '#1f242b';
      ctx.font = '17px Arial, sans-serif';
      m.commentLines.forEach((line, i) => {
        ctx.fillText(line, x + m.colNumW + padX, y + padY + 44 + i * lineHeight);
      });

      y += m.commentRowH;
    }

    return y;
  }

  private drawCheckboxItem(ctx: CanvasRenderingContext2D, x: number, y: number, label: string, checked: boolean): void {
    const boxSize = 18;
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#343a40';
    ctx.lineWidth = 1.5;
    ctx.fillRect(x, y, boxSize, boxSize);
    ctx.strokeRect(x, y, boxSize, boxSize);

    if (checked) {
      ctx.beginPath();
      ctx.moveTo(x + 3, y + 3);
      ctx.lineTo(x + boxSize - 3, y + boxSize - 3);
      ctx.moveTo(x + boxSize - 3, y + 3);
      ctx.lineTo(x + 3, y + boxSize - 3);
      ctx.strokeStyle = '#1f242b';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    ctx.fillStyle = '#1f242b';
    ctx.font = checked ? 'bold 17px Arial, sans-serif' : '17px Arial, sans-serif';
    ctx.fillText(label, x + boxSize + 10, y + 15);
  }

  private drawStatusBadge(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, success: boolean): void {
    ctx.font = 'bold 13px Arial, sans-serif';
    const textW = ctx.measureText(text).width;
    const badgeW = textW + 20;
    const badgeH = 26;

    ctx.fillStyle = success ? '#eaf5ec' : '#f1f3f5';
    ctx.strokeStyle = success ? '#2f7d41' : '#868e96';
    ctx.lineWidth = 1.5;
    ctx.fillRect(x, y, badgeW, badgeH);
    ctx.strokeRect(x, y, badgeW, badgeH);

    ctx.fillStyle = success ? '#1e5e2f' : '#495057';
    ctx.fillText(text, x + 10, y + 18);
  }

  private drawSignatureBlock(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, fullName: string, dateStr: string): number {
    const col1W = Math.floor(width * 0.42);
    const col2W = Math.floor(width * 0.33);
    const col3W = width - col1W - col2W;
    const rowH = 68;

    this.drawCellRect(ctx, x, y, col1W, rowH, '#ffffff', '#b8bec7');
    this.drawCellRect(ctx, x + col1W, y, col2W, rowH, '#ffffff', '#b8bec7');
    this.drawCellRect(ctx, x + col1W + col2W, y, col3W, rowH, '#ffffff', '#b8bec7');

    ctx.fillStyle = '#495057';
    ctx.font = '15px Arial, sans-serif';
    ctx.fillText('Исполнитель (ФИО)', x + 14, y + 24);
    ctx.fillText('Подпись', x + col1W + 14, y + 24);
    ctx.fillText('Дата', x + col1W + col2W + 14, y + 24);

    ctx.fillStyle = '#1f242b';
    ctx.font = 'bold 17px Arial, sans-serif';
    ctx.fillText(fullName || '—', x + 14, y + 50);
    ctx.fillText(dateStr, x + col1W + col2W + 14, y + 50);

    // Signature line
    ctx.strokeStyle = '#adb5bd';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + col1W + 14, y + 52);
    ctx.lineTo(x + col1W + col2W - 24, y + 52);
    ctx.stroke();

    return y + rowH;
  }

  private drawCellRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, fill: string, stroke: string): void {
    ctx.fillStyle = fill;
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, w, h);
  }

  private wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const paragraphs = String(text || '').split(/\r?\n/);
    const lines: string[] = [];

    const breakLongWord = (word: string): string[] => {
      const parts: string[] = [];
      let chunk = '';
      for (const ch of word) {
        const testChunk = chunk + ch;
        if (ctx.measureText(testChunk).width > maxWidth && chunk.length > 0) {
          parts.push(chunk);
          chunk = ch;
        } else {
          chunk = testChunk;
        }
      }
      if (chunk) {
        parts.push(chunk);
      }
      return parts;
    };

    for (const paragraph of paragraphs) {
      const words = paragraph.split(/\s+/).filter(Boolean);
      if (!words.length) {
        lines.push('');
        continue;
      }

      let currentLine = '';
      for (const word of words) {
        if (ctx.measureText(word).width > maxWidth) {
          if (currentLine) {
            lines.push(currentLine);
            currentLine = '';
          }
          const brokenParts = breakLongWord(word);
          for (let i = 0; i < brokenParts.length - 1; i++) {
            lines.push(brokenParts[i]);
          }
          currentLine = brokenParts[brokenParts.length - 1] || '';
        } else if (!currentLine) {
          currentLine = word;
        } else {
          const testLine = `${currentLine} ${word}`;
          if (ctx.measureText(testLine).width <= maxWidth) {
            currentLine = testLine;
          } else {
            lines.push(currentLine);
            currentLine = word;
          }
        }
      }
      if (currentLine) {
        lines.push(currentLine);
      }
    }

    return lines.length ? lines : ['—'];
  }

  private formatNowDate(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private canvasToJpegBytes(canvas: HTMLCanvasElement, quality: number): Uint8Array {
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    const base64 = dataUrl.split(',')[1];
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private assemblePdfFromJpegs(jpegPages: Uint8Array[], imgW: number, imgH: number): Blob {
    // Standard A4 size in PDF points (595.28 x 841.89)
    const pdfW = 595.28;
    const pdfH = 841.89;
    const chunks: Array<Uint8Array | string> = [];
    const offsets: number[] = [];
    let currentLength = 0;

    const encoder = new TextEncoder();
    const pushStr = (str: string) => {
      const bytes = encoder.encode(str);
      chunks.push(bytes);
      currentLength += bytes.length;
    };
    const pushBytes = (bytes: Uint8Array) => {
      chunks.push(bytes);
      currentLength += bytes.length;
    };

    const numPages = jpegPages.length;
    const totalObjects = 2 + numPages * 3;

    pushStr('%PDF-1.4\n%\xFF\xFF\xFF\xFF\n');

    // 1: Catalog
    offsets[1] = currentLength;
    pushStr('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');

    // 2: Pages
    const kids = jpegPages.map((_, i) => `${3 + i * 3} 0 R`).join(' ');
    offsets[2] = currentLength;
    pushStr(`2 0 obj\n<< /Type /Pages /Kids [ ${kids} ] /Count ${numPages} >>\nendobj\n`);

    jpegPages.forEach((jpegBytes, i) => {
      const pageObjId = 3 + i * 3;
      const contentObjId = 4 + i * 3;
      const imgObjId = 5 + i * 3;
      const imgName = `/Im${i}`;

      const contentStream = `q\n${pdfW.toFixed(2)} 0 0 ${pdfH.toFixed(2)} 0 0 cm\n${imgName} Do\nQ\n`;
      const contentBytes = encoder.encode(contentStream);

      // Page object
      offsets[pageObjId] = currentLength;
      pushStr(
        `${pageObjId} 0 obj\n` +
          `<< /Type /Page /Parent 2 0 R /MediaBox [ 0 0 ${pdfW.toFixed(2)} ${pdfH.toFixed(2)} ] ` +
          `/Resources << /XObject << ${imgName} ${imgObjId} 0 R >> >> ` +
          `/Contents ${contentObjId} 0 R >>\n` +
          `endobj\n`
      );

      // Content object
      offsets[contentObjId] = currentLength;
      pushStr(`${contentObjId} 0 obj\n<< /Length ${contentBytes.length} >>\nstream\n`);
      pushBytes(contentBytes);
      pushStr('\nendstream\nendobj\n');

      // Image XObject
      offsets[imgObjId] = currentLength;
      pushStr(
        `${imgObjId} 0 obj\n` +
          `<< /Type /XObject /Subtype /Image /Width ${imgW} /Height ${imgH} ` +
          `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpegBytes.length} >>\n` +
          `stream\n`
      );
      pushBytes(jpegBytes);
      pushStr('\nendstream\nendobj\n');
    });

    const xrefOffset = currentLength;
    pushStr(`xref\n0 ${totalObjects + 1}\n`);
    pushStr('0000000000 65535 f \n');
    for (let objId = 1; objId <= totalObjects; objId++) {
      const offsetStr = String(offsets[objId]).padStart(10, '0');
      pushStr(`${offsetStr} 00000 n \n`);
    }

    pushStr(`trailer\n<< /Size ${totalObjects + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

    return new Blob(chunks as BlobPart[], { type: 'application/pdf' });
  }
}
